import React, { useState } from 'react';
import { Button, Card, Row, Col, Badge, ProgressBar, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faMagic, 
  faVolumeUp, 
  faWaveSquare, 
  faSliders, 
  faCompactDisc, 
  faClock, 
  faAdjust,
  faPlay,
  faFileAudio,
  faMicrochip,
  faHeadphones,
  faExpandArrowsAlt,
  faFilter,
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAudioPlayer } from "../context/AudioContext";
import { useParams } from 'react-router-dom';

const AutoMasterData = ({ asset, showHeader = false }) => {
  const { projectId } = useParams();
  
  // Check if mastering failed - improved error detection
  const masteringResult = asset.metadata?.result?.mastering || asset.metadata?.mastering;
  const hasMasteringError = masteringResult?.status === 'error' || masteringResult?.error || 
                           (asset.status === 'ERROR' && asset.metadata?.error_message);
  const masteringError = masteringResult?.error || asset.metadata?.error_message || 
                        (asset.status === 'ERROR' ? 'Mastering process failed' : null);
  
  // Check if asset is still processing
  const isProcessing = asset.status === 'PROCESSING' || asset.status === 'PENDING' || 
                      (!asset.download_url && !hasMasteringError && asset.status !== 'ERROR');
  
  // Extract mastering parameters from various possible locations
  const masteringData = asset.metadata?.result || asset.metadata || asset.mastering || {};
  const inputLoudness = masteringData.input_loudness || masteringData.inputLoudness;
  const targetLoudness = masteringData.target_loudness || masteringData.targetLoudness || masteringData.mastering_intensity;
  const outputLoudness = masteringData.output_loudness || masteringData.outputLoudness;
  const lowCutFreq = masteringData.low_cut_frequency || masteringData.lowCutFrequency;
  const highCutFreq = masteringData.high_cut_frequency || masteringData.highCutFrequency;
  const preserveBass = masteringData.preserve_bass || masteringData.preserveBass;
  const bitDepth = masteringData.bit_depth || masteringData.bitDepth || asset.metadata?.bit_depth;
  const sampleRate = masteringData.sample_rate || masteringData.sampleRate || asset.metadata?.sample_rate;
  const ceiling = masteringData.ceiling || masteringData.ceiling_dbfs;
  const processingMode = masteringData.processing_mode || masteringData.processingMode;
  
  // File information - use actual asset properties for mastered file
  const fileName = asset.file_name;
  const format = asset.format;
  const duration = asset.duration;
  const fileSize = asset.file_size;
  
  // Additional metadata that might be extracted from the mastered file
  const bitrate = asset.metadata?.bitrate || asset.bit_rate;
  const channels = asset.metadata?.channels;
  const extractedSampleRate = asset.metadata?.sample_rate || asset.sample_rate;
  
  // Download URLs
  const downloadUrl = asset.download_url;
  const metadataUrl = masteringData.metadata_file || asset.metadata?.file_url;

  const { playAudio } = useAudioPlayer();

  const formatDuration = (seconds) => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const mb = (bytes / (1024 * 1024)).toFixed(1);
    return `${mb} MB`;
  };

  const InfoCard = ({ icon, label, value, unit = '', color = 'var(--purple-accent)', showProgressBar = false, progressValue = 0, progressMax = 100 }) => {
    if (value === null || value === undefined) return null;
    return (
      <div 
        className="p-3 rounded mb-3"
        style={{
          backgroundColor: 'rgba(179, 95, 208, 0.03)',
          border: '1px solid rgba(179, 95, 208, 0.15)',
          borderRadius: '8px'
        }}
      >
        <div className="d-flex align-items-center">
          <FontAwesomeIcon 
            icon={icon} 
            className="me-3" 
            style={{ 
              color: color, 
              fontSize: 'var(--font-lg)',
              minWidth: '20px'
            }} 
          />
          <div className="flex-grow-1">
            <div className="small text-muted mb-1">{label}</div>
            <div style={{ color: '#d9d9d9', fontWeight: '500' }}>
              {value}{unit}
            </div>
            {showProgressBar && (
              <ProgressBar 
                now={Math.abs(progressValue)} 
                max={progressMax} 
                className="mt-2" 
                style={{ height: '6px' }}
                variant="info"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const handlePlayAudio = () => {
    if (asset.download_url) {
      playAudio(asset.download_url, asset.file_name, projectId);
    }
  };

  // If still processing, show processing state
  if (isProcessing) {
    return (
      <div 
        className="mt-3 p-4 rounded" 
        style={{ 
          backgroundColor: 'rgba(255, 193, 7, 0.08)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 193, 7, 0.2)',
          borderRadius: '12px',
          color: '#d9d9d9'
        }}
      >
        <div className="text-center mb-4">
          <FontAwesomeIcon 
            icon={faClock} 
            className="mb-2 fa-spin" 
            style={{ 
              color: '#ffc107', 
              fontSize: 'var(--font-3xl)' 
            }} 
          />
          <h5 className="mb-2" style={{ color: '#d9d9d9' }}>Auto Mastering in Progress</h5>
          <Alert variant="info" className="mb-3">
            <FontAwesomeIcon icon={faClock} className="me-2" />
            Your audio is being processed. This usually takes 1-3 minutes.
          </Alert>
        </div>

        {/* Show processing metadata */}
        <div className="mb-3">
          <h6 className="mb-3" style={{ color: '#d9d9d9' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" style={{ color: 'var(--purple-accent)' }} />
            Processing Details
          </h6>
          
          <div className="small text-muted">
            <div><strong>File:</strong> {fileName}</div>
            <div><strong>Status:</strong> {asset.status}</div>
            <div><strong>Workflow:</strong> {asset.metadata?.workflow || 'Auto Master'}</div>
            {asset.metadata?.music_ai_job_id && (
              <div><strong>Job ID:</strong> {asset.metadata.music_ai_job_id}</div>
            )}
            <div><strong>Started:</strong> {asset.metadata?.generation_timestamp 
              ? new Date(asset.metadata.generation_timestamp).toLocaleString() 
              : new Date(asset.created_at).toLocaleString()}</div>
          </div>
        </div>

        <div className="text-center">
          <small className="text-muted">
            The page will automatically update when processing is complete.
          </small>
        </div>
      </div>
    );
  }

  // If mastering failed, show error state
  if (hasMasteringError) {
    return (
      <div 
        className="mt-3 p-4 rounded" 
        style={{ 
          backgroundColor: 'rgba(220, 53, 69, 0.08)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(220, 53, 69, 0.2)',
          borderRadius: '12px',
          color: '#d9d9d9'
        }}
      >
        <div className="text-center mb-4">
          <FontAwesomeIcon 
            icon={faExclamationTriangle} 
            className="mb-2" 
            style={{ 
              color: '#dc3545', 
              fontSize: 'var(--font-3xl)' 
            }} 
          />
          <h5 className="mb-2" style={{ color: '#d9d9d9' }}>Auto Mastering Failed</h5>
          <Alert variant="danger" className="mb-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {masteringError || 'An error occurred during the mastering process'}
          </Alert>
        </div>

        {/* Show available metadata */}
        <div className="mb-3">
          <h6 className="mb-3" style={{ color: '#d9d9d9' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" style={{ color: 'var(--purple-accent)' }} />
            Processing Details
          </h6>
          
          <div className="small text-muted">
            <div><strong>File:</strong> {fileName}</div>
            <div><strong>Workflow:</strong> {asset.metadata?.workflow || 'Auto Master'}</div>
            {asset.metadata?.music_ai_job_id && (
              <div><strong>Job ID:</strong> {asset.metadata.music_ai_job_id}</div>
            )}
            <div><strong>Started:</strong> {asset.metadata?.generation_timestamp 
              ? new Date(asset.metadata.generation_timestamp).toLocaleString() 
              : new Date(asset.created_at).toLocaleString()}</div>
            {asset.metadata?.completed_at && (
              <div><strong>Failed At:</strong> {new Date(asset.metadata.completed_at).toLocaleString()}</div>
            )}
            <div><strong>Status:</strong> {asset.status}</div>
          </div>
        </div>

        <div className="text-center">
          <small className="text-muted">
            Please try generating a new auto master or contact support if the problem persists.
          </small>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="mt-3 p-4 rounded" 
      style={{ 
        backgroundColor: 'rgba(179, 95, 208, 0.08)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(179, 95, 208, 0.2)',
        borderRadius: '12px',
        color: '#d9d9d9'
      }}
    >
      {showHeader && (
        <div className="mb-4 text-center">
          <FontAwesomeIcon 
            icon={faMagic} 
            className="mb-2" 
            style={{ 
              color: 'var(--purple-accent)', 
              fontSize: 'var(--font-3xl)' 
            }} 
          />
          <h5 className="mb-0" style={{ color: '#d9d9d9' }}>Auto Mastering</h5>
        </div>
      )}

      {/* Show warning if no download URL available but not processing or errored */}
      {!downloadUrl && !isProcessing && !hasMasteringError && (
        <Alert variant="warning" className="mb-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          Mastered file is not available for download. The file may have been processed but the download link is missing. Please try refreshing the page or contact support.
        </Alert>
      )}

      {/* Main Track Info */}
      <div 
        className="p-4 rounded mb-4"
        style={{
          backgroundColor: 'rgba(179, 95, 208, 0.05)',
          border: '1px solid rgba(179, 95, 208, 0.18)',
          borderRadius: '10px'
        }}
      >
        <div className="text-center mb-3">
          <h4 className="mb-2" style={{ color: '#d9d9d9', fontWeight: '600' }}>{fileName}</h4>
          <h6 className="mb-0 text-muted">AI Enhanced Master</h6>
        </div>
        
        <Row className="text-center">
          <Col xs={6} md={3}>
            <div className="mb-2">
              <FontAwesomeIcon icon={faFileAudio} style={{ color: 'var(--purple-accent)' }} />
              <div className="small text-muted mt-1">Format</div>
              <div className="small" style={{ color: '#d9d9d9' }}>{format?.toUpperCase() || 'Unknown'}</div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="mb-2">
              <FontAwesomeIcon icon={faClock} style={{ color: 'var(--purple-accent)' }} />
              <div className="small text-muted mt-1">Duration</div>
              <div className="small" style={{ color: '#d9d9d9' }}>{formatDuration(duration)}</div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="mb-2">
              <FontAwesomeIcon icon={faCompactDisc} style={{ color: 'var(--purple-accent)' }} />
              <div className="small text-muted mt-1">Size</div>
              <div className="small" style={{ color: '#d9d9d9' }}>{formatFileSize(fileSize)}</div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="mb-2">
              <FontAwesomeIcon icon={faMagic} style={{ color: 'var(--purple-accent)' }} />
              <div className="small text-muted mt-1">Status</div>
              <div className="small" style={{ color: '#d9d9d9' }}>{downloadUrl ? 'Enhanced' : 'Processing'}</div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Processing Metadata */}
      <div className="mb-4">
        <h6 className="mb-3" style={{ color: '#d9d9d9' }}>
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" style={{ color: 'var(--purple-accent)' }} />
          Processing Information
        </h6>
        
        <div 
          className="p-3 rounded"
          style={{
            backgroundColor: 'rgba(179, 95, 208, 0.03)',
            border: '1px solid rgba(179, 95, 208, 0.15)',
            borderRadius: '8px'
          }}
        >
          <Row className="small">
            <Col md={6}>
              <div className="mb-2">
                <span className="text-muted">Workflow:</span>
                <span className="ms-2" style={{ color: '#d9d9d9' }}>
                  {asset.metadata?.workflow || 'Auto Master'}
                </span>
              </div>
              <div className="mb-2">
                <span className="text-muted">Job ID:</span>
                <span className="ms-2" style={{ color: '#d9d9d9', fontFamily: 'monospace', fontSize: '0.8em' }}>
                  {asset.metadata?.music_ai_job_id || 'Unknown'}
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-2">
                <span className="text-muted">Started:</span>
                <span className="ms-2" style={{ color: '#d9d9d9' }}>
                  {asset.metadata?.generation_timestamp 
                    ? new Date(asset.metadata.generation_timestamp).toLocaleString() 
                    : new Date(asset.created_at).toLocaleString()}
                </span>
              </div>
              <div className="mb-2">
                <span className="text-muted">Completed:</span>
                <span className="ms-2" style={{ color: '#d9d9d9' }}>
                  {asset.metadata?.completed_at 
                    ? new Date(asset.metadata.completed_at).toLocaleString() 
                    : 'In Progress'}
                </span>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Show mastering parameters if available */}
      {(targetLoudness || inputLoudness || outputLoudness || lowCutFreq || highCutFreq || bitDepth || extractedSampleRate) && (
        <div className="mb-4">
          <h6 className="mb-3" style={{ color: '#d9d9d9' }}>
            <FontAwesomeIcon icon={faSliders} className="me-2" style={{ color: 'var(--purple-accent)' }} />
            Mastering Parameters
          </h6>
          
          <Row>
            <Col md={6}>
              <InfoCard 
                icon={faVolumeUp} 
                label="Target Loudness" 
                value={targetLoudness} 
                unit=" LUFS"
                showProgressBar={targetLoudness !== null && targetLoudness !== undefined}
                progressValue={Math.abs(targetLoudness || -14)}
                progressMax={30}
              />
              <InfoCard 
                icon={faFilter} 
                label="Low Cut Frequency" 
                value={lowCutFreq} 
                unit=" Hz"
              />
              <InfoCard 
                icon={faMicrochip} 
                label="Bit Depth" 
                value={bitDepth} 
                unit=" bit"
              />
              {ceiling && (
                <InfoCard 
                  icon={faAdjust} 
                  label="Ceiling" 
                  value={ceiling} 
                  unit=" dBFS"
                />
              )}
            </Col>
            <Col md={6}>
              {outputLoudness && (
                <InfoCard 
                  icon={faSliders} 
                  label="Output Loudness" 
                  value={outputLoudness} 
                  unit=" LUFS"
                  showProgressBar={true}
                  progressValue={Math.abs(outputLoudness)}
                  progressMax={30}
                />
              )}
              <InfoCard 
                icon={faFilter} 
                label="High Cut Frequency" 
                value={highCutFreq} 
                unit=" Hz"
              />
              <InfoCard 
                icon={faWaveSquare} 
                label="Sample Rate" 
                value={extractedSampleRate || sampleRate} 
                unit=" Hz"
              />
              {bitrate && (
                <InfoCard 
                  icon={faVolumeUp} 
                  label="Bitrate" 
                  value={bitrate} 
                  unit=" kbps"
                />
              )}
              {channels && (
                <InfoCard 
                  icon={faHeadphones} 
                  label="Channels" 
                  value={channels === 1 ? "Mono" : channels === 2 ? "Stereo" : `${channels} Channel`} 
                />
              )}
              {preserveBass !== undefined && (
                <InfoCard 
                  icon={faHeadphones} 
                  label="Bass Preservation" 
                  value={preserveBass ? "Enabled" : "Disabled"} 
                />
              )}
            </Col>
          </Row>
        </div>
      )}

      {/* Processing Info */}
      {(inputLoudness || processingMode) && (
        <div className="mb-4">
          <h6 className="mb-3" style={{ color: '#d9d9d9' }}>
            <FontAwesomeIcon icon={faMagic} className="me-2" style={{ color: 'var(--purple-accent)' }} />
            Processing Details
          </h6>
          
          <Row>
            <Col md={6}>
              {inputLoudness && (
                <InfoCard 
                  icon={faVolumeUp} 
                  label="Input Loudness" 
                  value={inputLoudness} 
                  unit=" LUFS"
                />
              )}
            </Col>
            <Col md={6}>
              {processingMode && (
                <InfoCard 
                  icon={faSliders} 
                  label="Processing Mode" 
                  value={processingMode} 
                />
              )}
            </Col>
          </Row>
        </div>
      )}

      {/* Enhancement Features - Always show these as they represent what auto mastering does */}
      <div className="mb-4">
        <h6 className="mb-3" style={{ color: '#d9d9d9' }}>
          <FontAwesomeIcon icon={faExpandArrowsAlt} className="me-2" style={{ color: 'var(--purple-accent)' }} />
          Applied Enhancements
        </h6>
        
        <Row>
          <Col md={6} className="mb-2">
            <div 
              className="p-2 rounded text-center"
              style={{
                backgroundColor: 'rgba(179, 95, 208, 0.05)',
                border: '1px solid rgba(179, 95, 208, 0.15)'
              }}
            >
              <FontAwesomeIcon icon={faVolumeUp} className="me-2" style={{ color: 'var(--purple-accent)' }} />
              <small style={{ color: '#d9d9d9' }}>Loudness Optimization</small>
            </div>
          </Col>
          <Col md={6} className="mb-2">
            <div 
              className="p-2 rounded text-center"
              style={{
                backgroundColor: 'rgba(179, 95, 208, 0.05)',
                border: '1px solid rgba(179, 95, 208, 0.15)'
              }}
            >
              <FontAwesomeIcon icon={faSliders} className="me-2" style={{ color: 'var(--purple-accent)' }} />
              <small style={{ color: '#d9d9d9' }}>EQ Enhancement</small>
            </div>
          </Col>
          <Col md={6} className="mb-2">
            <div 
              className="p-2 rounded text-center"
              style={{
                backgroundColor: 'rgba(179, 95, 208, 0.05)',
                border: '1px solid rgba(179, 95, 208, 0.15)'
              }}
            >
              <FontAwesomeIcon icon={faAdjust} className="me-2" style={{ color: 'var(--purple-accent)' }} />
              <small style={{ color: '#d9d9d9' }}>Dynamic Range Control</small>
            </div>
          </Col>
          <Col md={6} className="mb-2">
            <div 
              className="p-2 rounded text-center"
              style={{
                backgroundColor: 'rgba(179, 95, 208, 0.05)',
                border: '1px solid rgba(179, 95, 208, 0.15)'
              }}
            >
              <FontAwesomeIcon icon={faExpandArrowsAlt} className="me-2" style={{ color: 'var(--purple-accent)' }} />
              <small style={{ color: '#d9d9d9' }}>Stereo Imaging</small>
            </div>
          </Col>
        </Row>
      </div>

      {/* Action Buttons */}
      <div className="text-center mt-4">
        <div className="d-flex gap-2 justify-content-center flex-wrap">
          {downloadUrl ? (
            <>
              <Button 
                onClick={handlePlayAudio}
                className="beats-btn-purple"
                size="sm"
              >
                <FontAwesomeIcon icon={faPlay} className="me-2" />
                Play Mastered Audio
              </Button>
              <Button 
                as="a" 
                href={downloadUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                variant="outline-light"
                className="beats-btn-outline"
                size="sm"
              >
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Download Master
              </Button>
            </>
          ) : (
            <div className="text-muted small">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Mastered file will be available once processing is complete
            </div>
          )}
          
          {metadataUrl && (
            <Button 
              as="a" 
              href={metadataUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              variant="outline-light"
              className="beats-btn-outline"
              size="sm"
            >
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Download Settings
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoMasterData; 